import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  CustomAttributeEntityType,
  enumCustomAttributeEntityType,
} from '@rematter/frontend-lib-api-client';
import { Button, cn, Row } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/customAttributes.state';
import { useTranslations } from '../../i18n/useTranslations';

import { CustomAttributeCard } from './CustomAttributeCard';

export const CustomAttributes = () => {
  const t = useTranslations();
  const customAttributesByEntityType = useAtomValue(state.$customAttributes);
  const setShowCreationModal = useSetAtom(state.$showCreationDialog);
  const [, updateCustomAttribute] = useAtom(state.$updateCustomAttribute);

  const [entityTypeFilter, setEntityTypeFilter] = useAtom(state.$entityType);

  const customAttributes =
    (entityTypeFilter &&
      customAttributesByEntityType.data?.[entityTypeFilter]) ??
    [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = customAttributes.findIndex((it) => it.id === active.id);
      const newIndex = customAttributes.findIndex((it) => it.id === over?.id);

      arrayMove(customAttributes, oldIndex, newIndex).forEach((it, index) => {
        if (index !== it.order) {
          updateCustomAttribute({
            ...it,
            order: index,
          });
        }
      });
    }
  };

  return (
    <div className="@CustomAttributes flex h-full">
      <div className="w-[250px] border-r border-solid border-neutral/10 bg-white">
        <div className="border-b border-solid border-neutral/10 px-3 py-2 text-xs text-neutral/40">
          {t.customAttributesView.entityTypes}
        </div>
        {Object.values(enumCustomAttributeEntityType).map((entity) => {
          const isSelected = entity === entityTypeFilter;

          return (
            <button
              key={entity}
              className={cn(
                'flex w-full items-center justify-between px-3 py-2 text-left text-sm outline-none hover:bg-neutral/10',
                isSelected && 'bg-neutral/60 text-white hover:bg-neutral/60',
              )}
              onClick={(e) => {
                e.preventDefault();
                setEntityTypeFilter(entity);
              }}
            >
              <span>
                {t.customAttributesView.entityTypeLabels[entity] ?? entity}
              </span>
              {customAttributesByEntityType.data?.[
                entity as CustomAttributeEntityType
              ]?.length && (
                <div
                  key={entity}
                  className={cn(
                    'min-w-[25px] rounded bg-neutral/10 px-2 py-1 text-center text-xs',
                    isSelected && 'bg-white/20 text-white',
                  )}
                >
                  {
                    customAttributesByEntityType.data?.[
                      entity as CustomAttributeEntityType
                    ]?.length
                  }
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex w-full flex-col gap-4 bg-neutral/5 p-4">
        <Button
          className={'self-start'}
          onClick={() => setShowCreationModal(true)}
        >
          {t.customAttributesView.addCustomAttribute}
        </Button>
        <div className="flex flex-col gap-2">
          {customAttributes.length === 0 && (
            <Row
              className="h-40 w-full rounded-lg bg-space text-sm text-neutral/50 shadow"
              xCenter
            >
              No custom attributes found for this entity type
            </Row>
          )}
          {customAttributes.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={customAttributes}
                strategy={verticalListSortingStrategy}
              >
                {customAttributes?.map((it) => (
                  <CustomAttributeCard key={it.id} customAttribute={it} />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    </div>
  );
};
